import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { fillTokens, playerTokens } from '../core/TextTokens';
import { rankSystem } from './RankSystem';
import {
    NETIZEN_REVIEWS, REVIEWER_DISPLAY_NAME,
    ReviewEntry, ReviewFace,
} from '../data/tables/NetizenReviews';

/**
 * 网友评价系统（批 C 功能先行版，设计=docs/新增功能-年度奖项与网友评价.md §5）。
 * 文案取自 `NetizenReviews.ts`（四面全是用户亲笔正式文案）。
 *
 * 规则（全部对齐设计文档）：
 *  - 分档：颜值气质 = (charm+temperament)/2、演技 = talent，阈值 100/200/300/400；咖位面按 rank 五档；
 *    口碑面按显示值域 −500~500 五档（边界 −300／−100／101／301，**T3 那一档⛔ 有文案＝筛空**，见 `repBand()`）
 *  - 可见性：Lv.1 ⛔ 评价（见 `unlocked()`）；Lv.2 只出咖位面（网上刚开始有这人，具体评价都是假的）；Lv.3 起全开
 *  - 🚨 **事件桶整块下线（2026-10-07 两道令）**：先「公益爆红和扑街删掉」→「连机制一起拆吧，因为你写的判据不太合理」，
 *    再「获奖和塌房的评论库先不写 游戏里也不要实装」⇒ 五桶全撤，`activeEvents()`/`EventKind`/`evKind` 字段/`face:'event'`
 *    连同"事件命中保底 1 条"的抽取逻辑一起删。延后理由（用户原话）＝**这两类按咖位分档写才合理，工作量太大**。
 * ⇒ 日后重做事件桶＝先按咖位分档设计判据与文案，拿旧五条判据当方案
 *  - 🚨 **近作面整块下线（2026-10-31 茄子令）**：`face:'near'` 18 条+`nearBandOf` 分格+组池一并删（见 NetizenReviews.ts 头注释）
 *  - 抽取：每次 5 条、免费无限次；**同月固定一批**（2026-09-30 用户拍板改版：原「同月不重复」作废——
 *    重开面板是同一批，下个月第一次点开才重新抽）
 *  - 占位符（%玩家名% 等）：取不到对应数据的文案**整条跳过**（不残留 %）
 *  - 说话人（2026-10-06 用户令「全用随机网友」+ 同日追改「先让所有的网友名字都叫 momo」）：⛔ 人设，
 *    昵称一律＝`REVIEWER_DISPLAY_NAME`（momo）；随机网名池原地留着待恢复、当前⛔ 上屏
 */
export class ReviewSystem {
    private static instance: ReviewSystem;

    private constructor() { }

    static getInstance(): ReviewSystem {
        if (!this.instance) {
            this.instance = new ReviewSystem();
        }
        return this.instance;
    }

    /** 属性面档位（1~5）：阈值 100/200/300/400（门槛尺 ATTR_SCALE=5，单属性 0~500） */
    private attrBand(value: number): 1 | 2 | 3 | 4 | 5 {
        if (value >= 400) return 5;
        if (value >= 300) return 4;
        if (value >= 200) return 3;
        if (value >= 100) return 2;
        return 1;
    }

    /**
     * 咖位面档位（2026-10-06 用户改档）：**Lv.1 ⛔ 有评价**（见 `unlocked()`，档位只从 Lv.2 起算）
     * ⇒ Lv.2~3→T1 / 4~5→T2 / 6~7→T3 / 8~9→T4 / **Lv.10 独占 T5**。
     * ⚠️ 旧口径是两两一档从 Lv.1 起（1-2→T1…9-10→T5），本表整体下移一级、末档改独占。
     */
    private fameBand(rank: number): 1 | 2 | 3 | 4 | 5 {
        return Math.min(5, Math.floor((Math.max(rank, 2) - 2) / 2) + 1) as 1 | 2 | 3 | 4 | 5;
    }

    /**
     * 口碑面档位（2026-10-07 用户两道令定档）：读**显示值**（域 −500~500），边界 −300／−100／101／301
     * ⇒ T1 −500~−301／T2 −300~−101／T3 −100~100／T4 101~300／T5 301~500。
     * ⚠️ 中间档上下各宽 1 ⇒ **⛔ 等宽除法**（`clamp(floor((r+500)/200)+1,1,5)` 那版 10-07 同日作废），只能逐级比。
     * ⚠️ **T3 那一档⛔ 有文案**（用户「算了口碑-100~100就不设评价吧，感觉没什么好评的」）⇒ 档位照旧算得出 3、
     *    组池时筛出 0 条属设计内：**⛔ 特判跳过、⛔ 兜底补句子、⛔ 为它放宽别的档**（同 Lv.1 关门、近作面挂 filmography 那两个筛空形状）。
     */
    /**
     * 口碑五档（`T1~T5`）：边界取自 `GameConfig.REP_BAND_EDGES`（**唯一口径源**，2026-10-22 抽出，值不变）。
     * 随机黑帖那套按口碑分档的触发概率／评论差分也读同一份边界。
     */
    private repBand(reputation: number): 1 | 2 | 3 | 4 | 5 {
        const e = GameConfig.REP_BAND_EDGES;
        if (reputation <= e[0]) return 1;
        if (reputation <= e[1]) return 2;
        if (reputation <= e[2]) return 3;
        if (reputation <= e[3]) return 4;
        return 5;
    }

    /**
     * 网友评价开不开（2026-10-06 用户令「Lv.1 直接不显示网友评价」）：Lv.1 网上还搜不到人，
     * 面板⛔ 空转——小助理在菜单里就地回一句（`AssistPanel.visibleMenu` 给这项挂 `reply`）。
     */
    unlocked(): boolean {
        return GameState.getInstance().player.rank >= 2;
    }

    /**
     * 取本月 5 条评价（2026-09-30 用户拍板改版：**同月固定一批**——第一次抽取后缓存，
     * 同月内重开面板恒为同一批，下个月第一次点开才重新抽）。
     * 返回 { speaker 显示名, text }[]——占位符已替换、取不到数据的条目已剔除。
     */
    pickReviews(): { speaker: string; text: string }[] {
        const state = GameState.getInstance();
        const p = state.player;
        if (!this.unlocked()) return [];      // Lv.1 网上还搜不到人（10-06 用户令）⇒ ⛔ 抽批也⛔ 落缓存
        const nowAbs = p.year * 12 + p.month;
        // —— 同月固定一批：缓存命中直接返回（⛔ 每次重开面板都重抽）——
        if (p.reviewCache?.month === nowAbs && p.reviewCache.items.length > 0) {
            return p.reviewCache.items;
        }
        const lookBand = this.attrBand((p.attributes.charm + p.attributes.temperament) / 2);
        const actBand = this.attrBand(p.attributes.talent);
        const fameBand = this.fameBand(p.rank);
        const lowFame = p.rank <= 2;                       // Lv.2 只出咖位面（Lv.1 已在上面关门）

        // —— 组池 ——
        // ⚠️ 2026-10-07 事件桶整块下线（见文件头那条）⇒ 这里⛔ 再判 `face==='event'`
        const bands: Partial<Record<ReviewFace, 1 | 2 | 3 | 4 | 5>> = {
            look: lookBand, act: actBand, fame: fameBand,
            rep: this.repBand(state.getAttribute('reputation')),   // 10-07 导入口碑面；T3 档⛔ 有文案＝筛空属设计内
        };
        const pool = NETIZEN_REVIEWS.filter(e => {
            if (lowFame && e.face !== 'fame') return false; // Lv.2 只出咖位面
            const band = bands[e.face as ReviewFace];
            return band !== undefined && e.band === band;
        });

        // —— 抽 5 条（不放回＝同一批⛔ 出现两句一样的）——
        const picked: ReviewEntry[] = [];
        const rest = [...pool];
        while (picked.length < 5 && rest.length > 0) {
            const i = Math.floor(Math.random() * rest.length);
            picked.push(rest.splice(i, 1)[0]);
        }

        // —— 占位符替换（取不到数据 → 整条跳过）+ 缓存 ——
        const out: { speaker: string; text: string }[] = [];
        for (const e of picked) {
            const text = this.fill(e);
            if (text === null) continue;
            // 10-06 追改：一批⛔ 换人换名，五条全坐 momo（网名池原地留着待恢复，见 NetizenReviews.ts 那条）
            out.push({ speaker: REVIEWER_DISPLAY_NAME, text });
        }
        p.reviewCache = { month: nowAbs, items: out };
        return out;
    }

    /** 占位符替换：%玩家名%/%咖位名%/%粉丝数%；占位符存在但依赖数据取不到就整条跳过
     *  2026-10-16：替换逻辑搬到 `core/TextTokens.fillTokens`（与偶遇/探班台词同吃一把尺），纪律一字未改
     *  2026-10-31：%作品名%/%角色名% 随近作面下线（库里⛔ 再有这两个占位符的行），ctx 参数删 */
    private fill(e: ReviewEntry): string | null {
        const p = GameState.getInstance().player;
        return fillTokens(e.text, playerTokens(
            { name: p.name, rank: p.rank, fans: p.fans },
            rankSystem.getRankName(p.rank),
            {},
        ));
    }
}

/** 导出单例 */
export const reviewSystem = ReviewSystem.getInstance();
